import { useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useCreateOt } from '../../hooks/useOrdenesTrabajo';
import { Button } from '../../components/ui/Button';

/**
 * Auto-creates an OT for the given project and redirects immediately to
 * the OT page. No modal, no "no OT" empty state.
 */
export function CreateOtAutoPage() {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const createMutation = useCreateOt();
  const fired = useRef(false);

  useEffect(() => {
    if (!projectId || fired.current) return;
    fired.current = true;

    createMutation
      .mutateAsync(projectId)
      .then(() => {
        navigate(`/proyectos/${projectId}/orden-trabajo`, { replace: true });
      })
      .catch(() => {
        // error state handled below via mutation.isError
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  if (createMutation.isError) {
    const message = (createMutation.error as { response?: { data?: { error?: { message?: string } } } })
      ?.response?.data?.error?.message ?? 'Error al crear la Orden de Trabajo';

    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-4">
        <AlertTriangle className="h-10 w-10 text-red-400" />
        <p className="text-sm text-gray-600">{message}</p>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={() => navigate(`/proyectos/${projectId}`)}>
            Volver al proyecto
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              fired.current = false;
              createMutation
                .mutateAsync(projectId!)
                .then(() => navigate(`/proyectos/${projectId}/orden-trabajo`, { replace: true }))
                .catch(() => {});
            }}
          >
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3">
      <Loader2 className="h-8 w-8 animate-spin text-brand-blue" />
      <p className="text-sm text-gray-500">Creando Orden de Trabajo...</p>
    </div>
  );
}
